<script lang="ts">
	import { beforeUpdate } from 'svelte';
	// Design System Components
	import { SidepanelLayout, SidepanelTabs } from '@Shelly/shelly-design-system';
	// Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { sidePanelState, sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	// Configs
	import { devices_config } from '@devices/index';
	// Helpers
	import { handleSidePanelTabClick } from '../../sidepanel-tabs-helpers';
	import {
		get_mode,
		isRgbCct,
		real_id,
		isCctOnlyDevice,
		isXMODDevice,
		isRgbwwPm,
	} from 'src/helpers/interface_helpers';
	import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import {
		defaultGroupObject,
		isGroupModeChangeEligible,
		getModeChangeBlockingType,
	} from 'src/helpers/group_helpers';
	import { supportsColorModeChange } from 'src/helpers/night_mode_helpers';
	// Tab components
	import Main from './Tabs/Main.svelte';
	import SecondaryControls from './Tabs/SecondaryControls.svelte';
	import Effects from './Tabs/Effects.svelte';
	import Devices from './Tabs/Devices.svelte';
	import Events from './Tabs/Events.svelte';
	import Info from './Tabs/Info.svelte';
	// Components
	import AddEditGroupWizard from 'src/components/Groups/GroupWizard/AddEditGroupWizard.svelte';
	import { setSidePanelState } from '../../sidepanel-helpers';

	$: id = $sidePanelStateCombined?.id;
	$: group = { ...$groups[id], devices: [...new Set(($groups[id]?.devices ?? []).map(String))] };

	$: type = group?.type;
	$: groupDevices = group?.devices;
	$: memberStatusStore = createMemberStatusSnapshot(groupDevices ?? []);
	$: memberSettingsStore = createMemberSettingsSnapshot(groupDevices ?? []);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;
	$: name = group?.name;
	$: roomName = group?.roomn || '';

	// Build device list with channel support
	$: list = groupDevices
		?.map((id: string) => {
			let baseId = id;
			let channel = 0;
			if (id.includes('_')) {
				const idMatch = id.match(/^(.+)_(\d+)$/);
				if (idMatch) {
					baseId = idMatch[1];
					channel = parseInt(idMatch[2], 10);
				}
			}
			if ($devices[baseId]) {
				const channelDevice = $devices[id];
				const baseDevice = $devices[baseId];
				const device = channelDevice
					? { ...baseDevice, ...channelDevice, id, channel }
					: { ...baseDevice, id, channel };
				return device;
			}
			if (!$devices[baseId]) {
				return { id: id, deleted: true };
			}
		})
		?.filter((e) => e !== undefined);

	// Light group variables
	let lights_category_devices = [];
	let color_mode_lights = [];
	let white_channel_support_lights = [];
	let white_mode_lights = [];
	let change_mode_lights = [];
	$: active_mode = 'color';
	let effects_supported_lights = [];
	let saved_effects_enabler = false;
	let roller_position_devices = [];
	let hasRGBW2 = false;
	let isGroupModeSwitchEligible = false;
	let modeChangeBlockingType: string | null = null;

	// Tab configuration
	let tabIndex: number = 0;
	let tabs = [];
	let tabComponents = [];

	// Build initial tabs
	$: {
		tabs = [
			{
				icon: 'fat fa-sliders-simple',
				isActive: tabIndex === 0,
				disabled: false,
				id: 'main',
			},
		];
		tabComponents = [Main];
	}

	let showAddEditGroupModal = false;
	let editGroup = defaultGroupObject;

	// Process light devices
	beforeUpdate(() => {
		if (type === 'light') {
			lights_category_devices = list
				?.filter((device) => {
					const isLightCategory = device.category === 'light' || isCctOnlyDevice(device.type);
					let isXMODLight = false;
					if (isXMODDevice(device?.type)) {
						const realId = real_id(device.id);
						const status = memberStatus[realId];
						if (
							status?.sys?.ch &&
							Array.isArray(status.sys.ch) &&
							status.sys.ch.length > 0
						) {
							const channelIndex = device.channel ?? 0;
							const component = status.sys.ch[channelIndex] || status.sys.ch[0];
							if (component) {
								const componentType = component.split(':')[0];
								isXMODLight =
									componentType === 'light' ||
									componentType === 'cct' ||
									componentType === 'rgbw';
							}
						}
					}
					return (isLightCategory || isXMODLight) && memberStatus[real_id(device.id)]?.online;
				})
				.map((d) => {
					let mode = '';
					if (isXMODDevice(d?.type)) {
						const realId = real_id(d.id);
						const status = memberStatus[realId];
						if (
							status?.sys?.ch &&
							Array.isArray(status.sys.ch) &&
							status.sys.ch.length > 0
						) {
							const channelIndex = d.channel ?? 0;
							const component = status.sys.ch[channelIndex] || status.sys.ch[0];
							if (component) {
								const componentType = component.split(':')[0];
								if (componentType === 'cct') {
									mode = 'cct';
								} else if (componentType === 'rgbw' || componentType === 'light') {
									mode = 'rgbw';
								} else {
									mode = 'shell';
								}
							} else {
								mode = d.mode || 'shell';
							}
						} else {
							mode = d.mode || 'shell';
						}
					} else if (memberStatus[real_id(d.id)] && memberSettings[real_id(d.id)]) {
						const baseDeviceId = real_id(d.id);
						mode = get_mode(
							$devices[baseDeviceId],
							memberStatus[baseDeviceId],
							memberSettings[baseDeviceId],
						);
					} else {
						const baseDeviceId = real_id(d.id);
						mode = Object.keys(devices_config[$devices[baseDeviceId]?.type]?.modes || {})[0];
					}

					mode = isRgbwwPm(d.type) ? d.mode : mode;

					let slider;
					if (isXMODDevice(d?.type)) {
						if (mode === 'rgbw' || mode === 'cct') {
							slider = 'brightness';
						} else {
							slider = devices_config[d?.type]?.modes?.[mode]?.slider;
						}
					} else {
						slider = devices_config[d?.type]?.modes?.[mode]?.slider;
					}

					return {
						...d,
						mode,
						slider,
					};
				});

			color_mode_lights = lights_category_devices?.filter((d) => {
				const mode = get_mode(d, memberStatus[real_id(d.id)], memberSettings[real_id(d.id)]);
				const _isRgbCct =
					isRgbCct(d.type) &&
					devices_settings.getByRealId(d.id)?.[`${mode}:0`]?.mode === 'rgb';
				return (
					d.mode === 'color' ||
					d.mode === 'rgb' ||
					d.mode === 'rgbw' ||
					mode === 'rgb' ||
					mode === 'rgbw' ||
					_isRgbCct
				);
			});

			white_channel_support_lights = color_mode_lights?.filter((device) => {
				const mode = get_mode(
					device,
					memberStatus[real_id(device.id)],
					memberSettings[real_id(device.id)],
				);
				return devices_config[device?.type]?.modes?.[mode]?.has_white;
			});

			white_mode_lights = lights_category_devices?.filter((d) => {
				const mode = get_mode(d, memberStatus[real_id(d.id)], memberSettings[real_id(d.id)]);
				return (
					(d.mode === 'white' || d.mode === 'cct') &&
					devices_config[d?.type]?.modes?.[mode]?.color_temp
				);
			});

			hasRGBW2 = lights_category_devices?.some((d) => d.type.includes('RGBW2'));

			change_mode_lights = lights_category_devices?.filter((d) => {
				return supportsColorModeChange(d.type) && memberStatus[real_id(d.id)];
			});

			isGroupModeSwitchEligible = isGroupModeChangeEligible(lights_category_devices);
			modeChangeBlockingType = getModeChangeBlockingType(lights_category_devices);

			active_mode = white_mode_lights?.length > color_mode_lights?.length ? 'white' : 'color';

			effects_supported_lights = lights_category_devices?.filter((d) => {
				return (
					memberStatus[real_id(d.id)] &&
					devices_config[d.type]?.modes?.[
						get_mode(d, memberStatus[real_id(d.id)], memberSettings[real_id(d.id)])
					]?.effects
				);
			});
			saved_effects_enabler = effects_supported_lights.length > 0;

			// Build tabs for light groups
			tabs = [
				{
					icon: 'fat fa-sliders-simple',
					isActive: tabIndex === 0,
					disabled: false,
					id: 'main',
				},
				{
					icon: 'fat fa-lightbulb',
					isActive: tabIndex === 1,
					disabled: color_mode_lights.length < 1 && white_mode_lights.length < 1,
					id: 'secondary_controls',
				},
				{
					icon: 'fat fa-microchip',
					isActive: tabIndex === 2,
					disabled: false,
					id: 'devices',
				},
				{
					icon: 'fat fa-stars',
					isActive: tabIndex === 3,
					disabled: !saved_effects_enabler,
					id: 'effects',
				},
				{
					icon: 'fat fa-circle-info',
					isActive: tabIndex === 4,
					disabled: false,
					id: 'info',
				},
			];
			tabComponents = [Main, SecondaryControls, Devices, Effects, Info];
		} else if (type === 'roller') {
			roller_position_devices = list.filter((d) => d.category === 'roller');

			tabs = [
				{
					icon: 'fat fa-sliders-simple',
					isActive: tabIndex === 0,
					disabled: false,
					id: 'main',
				},
				{
					icon: 'fat fa-microchip',
					isActive: tabIndex === 1,
					disabled: false,
					id: 'devices',
				},
				{
					icon: 'fat fa-circle-info',
					isActive: tabIndex === 2,
					disabled: false,
					id: 'info',
				},
			];
			tabComponents = [Main, Devices, Info];
		} else {
			// Default tabs for other group types
			tabs = [
				{
					icon: 'fat fa-sliders-simple',
					isActive: tabIndex === 0,
					disabled: false,
					id: 'main',
				},
				{
					icon: 'fat fa-notebook',
					isActive: tabIndex === 1,
					disabled: false,
					id: 'events',
				},
				{
					icon: 'fat fa-circle-info',
					isActive: tabIndex === 2,
					disabled: false,
					id: 'info',
				},
			];
			tabComponents = [Main, Events, Info];
		}
	});

	const onTabClick = (e: CustomEvent) => {
		const selectedTabData = e.detail;
		tabIndex = selectedTabData.index;
		tabs = handleSidePanelTabClick(tabs, tabIndex);
	};

	const resetActiveTab = () => {
		tabIndex = 0;
		tabs = tabs.map((tab, i) => ({ ...tab, isActive: i === 0 }));
	};

	const handleGroupEdit = () => {
		editGroup = { ...group };
		showAddEditGroupModal = true;
	};

	const onFinishEdit = () => {
		setSidePanelState({ type: 'group', id: group.id, open: true });
	};

	$: !$sidePanelState.open ? resetActiveTab() : null;
</script>

{#if id !== '-1'}
	<SidepanelLayout
		title={name}
		subtitle={roomName}
		settingsIcon={canManageHomeEntities($homes.list) ? 'fat fa-pen' : undefined}
		onSettings={canManageHomeEntities($homes.list) ? handleGroupEdit : undefined}
		hideSettings={!canManageHomeEntities($homes.list)}
	>
		<svelte:component
			this={tabComponents?.[tabIndex]}
			{group}
			{type}
			{list}
			{lights_category_devices}
			{color_mode_lights}
			{white_channel_support_lights}
			{white_mode_lights}
			{change_mode_lights}
			{hasRGBW2}
			{isGroupModeSwitchEligible}
			{modeChangeBlockingType}
			{active_mode}
		/>

		<SidepanelTabs slot="tabs" {tabs} on:click={onTabClick} />
	</SidepanelLayout>
{/if}

{#if showAddEditGroupModal}
	<AddEditGroupWizard editMode {editGroup} bind:showAddEditGroupModal on:finish={onFinishEdit} />
{/if}
